// src/pages/products/CreateProductPage/CreateProductPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Package, 
  X, 
  AlertCircle, 
  Info,
  DollarSign,
  Tag,
  Settings,
  Image as ImageIcon
} from 'lucide-react';
import { createProduct } from '../../../services/api/products';
import { getMyUsage } from '../../../services/api/subscription';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAuth } from '../../../hooks/useAuth';
import QuotaWarning from '../../../components/common/Subscription/QuotaWarning';
import useFormData from './hooks/useFormData';
import BasicInfoTab from './components/BasicInfoTab';
import PricingTab from './components/PricingTab';
import AttributesTab from './components/AttributesTab';
import VariationsTab from './components/VariationsTab';
import ImagesTab from './components/ImagesTab';
import DropshippingSection from '../components/DropshippingSection';
import BundleEditor from '../../../components/product/BundleEditor/BundleEditor';
import { serializeBundleItems } from '../../../components/product/BundleEditor/bundleUtils';
import DropshippingVariationsSection from '../components/DropshippingVariationsSection';
import { getProviderStatus } from '../../../services/api/dropshipping';
import styles from './CreateProductPage.module.css';
import { hasPrice } from '../../../utils/priceUtils';

const CreateProductPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(true);
  const [providerStatus, setProviderStatus] = useState(null);
  const [dropshippingData, setDropshippingData] = useState({
    dropshippingEnabled: false,
    minimumSellingPrice: '',
    maximumSellingPrice: '',
    dropshippingPrice: '',
  });
  
  const {
    // State
    formData,
    attributes,
    shops,
    warehouses,
    errors,
    isLoadingData,
    activeTab,
    variationCombinations,
    expandedVariations,
    selectedVariations,
    showBulkActions,
    bulkShopAssignments,
    bulkPrices,
    bulkCostPrice,
    bulkWeight,
    skuValidation,

    // Setters
    setFormData,
    setErrors,
    setActiveTab,
    setShowBulkActions,
    setBulkCostPrice,
    setBulkWeight,

    // Utility functions
    getAvailableTabs,
    validateForm,

    // Handler functions
    handleInputChange,
    handleDescriptionChange,
    handleBundleChange,
    handleAttributesChange,
    handleAttributeValuesChange,
    handleCategorySelect,
    handleTagsChange,
    handleBrandChange,
    handleShopDataChange,
    handleOpeningStockChange,
    handleVariationChange,
    handleVariationShopDataChange,
    handleVariationStockChange,
    handleVariationImageChange,
    handleImageUpload,
    handleImageRemove,
    handleSetMainImage,

    // Variation management
    toggleVariationExpansion,
    toggleVariationSelection,
    selectAllVariations,
    deselectAllVariations,
    removeSelectedVariations,
    regenerateVariations,

    // Bulk actions
    handleBulkShopAssignment,
    handleBulkPriceChange,
    applyBulkActions,

    // Dropshipping variation handlers
    handleVariationDropshippingChange,
    applyBulkDropshipping,
  } = useFormData();

  // Fetch quota and provider status
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setIsLoadingQuota(true);
        const response = await getMyUsage();
        setQuotaInfo(response.usage?.products || null);
      } catch (err) {
        console.error('Failed to fetch quota:', err);
      } finally {
        setIsLoadingQuota(false);
      }
    };

    const fetchProviderStatus = async () => {
      try {
        const status = await getProviderStatus();
        setProviderStatus(status);
      } catch (err) {
        console.error('Failed to fetch provider status:', err);
      }
    };

    fetchQuota();
    fetchProviderStatus();
  }, []);

  // Available tabs based on product type
  const isBundle = formData.productType === 'bundle';
  const availableTabs = isBundle
    ? [
      { id: 'basic', label: t('products.basicInfo'), icon: Info },
      { id: 'bundle', label: t('products.bundleTab'), icon: Package },
    ]
    : [
      { id: 'basic', label: t('products.basicInfo'), icon: Info },
      { id: 'pricing', label: t('products.pricing'), icon: DollarSign },
      { id: 'attributes', label: t('products.attributes'), icon: Tag },
    ];

  if (formData.productType === 'variable') {
    availableTabs.push({ id: 'variations', label: 'Variations', icon: Settings });
  }

  availableTabs.push({ id: 'images', label: t('products.images'), icon: ImageIcon });

  const showDropshippingTab = providerStatus?.isProviderEnabled && providerStatus?.kycStatus === 'approved';

  if (showDropshippingTab && !isBundle) {
    availableTabs.push({ id: 'dropshipping', label: t('dropshipping.dropshipping'), icon: Settings });
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      handleImageUpload(index, files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const submitData = new FormData();
      
      // Basic product data
      submitData.append('name', formData.name.trim());
      if (formData.printableName.trim()) {
        submitData.append('printableName', formData.printableName.trim());
      }
      submitData.append('type', formData.productType);
      submitData.append('visibility', formData.visibility);
      
      if (formData.description.trim()) {
        submitData.append('description', formData.description.trim());
      }
      
      if (formData.buyingPrice) {
        submitData.append('costPrice', parseFloat(formData.buyingPrice));
      }
      
      if (formData.sku.trim()) {
        submitData.append('sku', formData.sku.trim());
      }
      
      if (formData.barcode.trim()) {
        submitData.append('barcode', formData.barcode.trim());
      }
      
      if (formData.brandId) {
        submitData.append('brandId', parseInt(formData.brandId));
      }
      
      if (formData.weight) {
        submitData.append('weight', parseFloat(formData.weight));
      }
      
      if (formData.categoryId) {
        submitData.append('categoryIds', JSON.stringify([parseInt(formData.categoryId)]));
      }
      
      if (formData.tagIds.length > 0) {
        submitData.append('tagIds', JSON.stringify(formData.tagIds));
      }
      
      if (formData.attributeIds.length > 0) {
        submitData.append('attributeIds', JSON.stringify(formData.attributeIds));
      }

      if (formData.attributeValueIds.length > 0) {
        submitData.append('attributeValueIds', JSON.stringify(formData.attributeValueIds));
      }
      
      if (isBundle) {
        submitData.append('bundleItems', JSON.stringify(serializeBundleItems(formData.bundleItems, formData.bundleShopIds)));
      } else if (formData.productType === 'variable' && formData.variations.length > 0) {
        const validVariations = formData.variations.map((variation, index) => {
          const variationData = {
            variationIndex: index,
            sku: variation.sku || '',
            attributeValueIds: variation.attributeValueIds,
            shopData: variation.shopData
              .filter(shop => shop.enabled && hasPrice(shop.salesPrice))
              .map(shop => {
                const shopData = {
                  shopId: shop.shopId,
                  salesPrice: parseFloat(shop.salesPrice),
                  offers: shop.offers || [],
                };

                if (shop.dropshippingPrice) {
                  shopData.dropshippingPrice = parseFloat(shop.dropshippingPrice);
                }

                if (shop.overrideDeliveryChargeEnabled && shop.overrideDeliveryCharge != null && shop.overrideDeliveryCharge !== '') {
                  shopData.overrideDeliveryCharge = parseFloat(shop.overrideDeliveryCharge);
                } else {
                  shopData.overrideDeliveryCharge = null;
                }

                return shopData;
              }),
            openingStock: variation.openingStock
              .filter(stock => stock.stock !== '' && stock.stock >= 0)
              .map(stock => ({
                warehouseId: stock.warehouseId,
                stock: parseInt(stock.stock)
              }))
          };

          if (variation.costPrice) {
            variationData.costPrice = variation.costPrice;
          }
          if (variation.barcode) {
            variationData.barcode = variation.barcode;
          }
          if (variation.weight) {
            variationData.weight = variation.weight;
          }

          // Dropshipping fields per variation (only when provider enabled)
          if (showDropshippingTab) {
            variationData.dropshippingEnabled = !!variation.dropshippingEnabled;
            if (variation.minimumSellingPrice !== '') {
              variationData.minimumSellingPrice = parseFloat(variation.minimumSellingPrice) || null;
            }
            if (variation.maximumSellingPrice !== '') {
              variationData.maximumSellingPrice = parseFloat(variation.maximumSellingPrice) || null;
            }
            if (variation.dropshippingPrice !== '') {
              variationData.dropshippingPrice = parseFloat(variation.dropshippingPrice) || null;
            }
          }

          return variationData;
        });
        
        submitData.append('variations', JSON.stringify(validVariations));

        formData.variations.forEach((variation, index) => {
          if (variation.variationImage) {
            submitData.append(`variationImages[${index}]`, variation.variationImage);
          }
        });

      } else {
        // For simple products
        const validShopData = formData.shopData
          .filter(shop => shop.enabled && hasPrice(shop.salesPrice))
          .map(shop => {
            const shopData = {
              shopId: shop.shopId,
              salesPrice: parseFloat(shop.salesPrice),
              offers: shop.offers || [],
            };

            if (shop.dropshippingPrice) {
              shopData.dropshippingPrice = parseFloat(shop.dropshippingPrice);
            }
            if (shop.overrideDeliveryChargeEnabled && shop.overrideDeliveryCharge != null && shop.overrideDeliveryCharge !== '') {
              shopData.overrideDeliveryCharge = parseFloat(shop.overrideDeliveryCharge);
            } else {
              shopData.overrideDeliveryCharge = null;
            }

            return shopData;
          });
        
        if (validShopData.length > 0) {
          submitData.append('shopData', JSON.stringify(validShopData));
        }

        const validOpeningStock = formData.openingStock
          .filter(stock => stock.stock !== '' && stock.stock >= 0)
          .map(stock => ({
            warehouseId: stock.warehouseId,
            stock: parseInt(stock.stock)
          }));
        
        if (validOpeningStock.length > 0) {
          submitData.append('openingStock', JSON.stringify(validOpeningStock));
        }
      }
      
      // Images
      const mainImage = formData.images[formData.mainImageIndex];
      if (mainImage) {
        submitData.append('mainImage', mainImage);
      }

      const galleryImages = formData.images.filter((img, index) => img !== null && index !== formData.mainImageIndex);
      galleryImages.forEach(image => {
        submitData.append('galleryImages', image);
      });

      if (showDropshippingTab && formData.productType === 'simple') {
        submitData.append('dropshippingEnabled', dropshippingData.dropshippingEnabled ? 'true' : 'false');
        if (dropshippingData.minimumSellingPrice !== '') {
          submitData.append('minimumSellingPrice', parseFloat(dropshippingData.minimumSellingPrice));
        }
        if (dropshippingData.maximumSellingPrice !== '') {
          submitData.append('maximumSellingPrice', parseFloat(dropshippingData.maximumSellingPrice));
        }
        if (dropshippingData.dropshippingPrice !== '') {
          submitData.append('dropshippingPrice', parseFloat(dropshippingData.dropshippingPrice));
        }
      }

      const response = await createProduct(submitData);
      success(t('products.productCreatedSuccess', { productName: response.product.name }), {
        title: t('common.success'),
        duration: 4000
      });
      navigate('/products');
    } catch (err) {
      console.error('Product creation error:', err);
      if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: t('products.failedToCreateProduct') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Determine quota severity
  const getQuotaSeverity = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return 'info';
    const percentage = quotaInfo.limit > 0 ? (quotaInfo.usage / quotaInfo.limit) * 100 : 0;
    if (percentage >= 90) return 'critical';
    if (percentage >= 80) return 'warning';
    return 'info';
  };

  // Check if quota is exceeded
  const isQuotaExceeded = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return false;
    return quotaInfo.usage >= quotaInfo.limit;
  };

  if (isLoadingData) {
    return (
      <div className={styles.createProductPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Package className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('products.createNewProduct')}</h1>
                <p>{t('products.addNewProductToInventory')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('products.loadingData')}</h3>
            <p>{t('products.loadingDataDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createProductPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Package className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('products.createNewProduct')}</h1>
              <p>{t('products.addNewProductToInventory')}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button 
              onClick={() => navigate('/products')} 
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('products.backToProducts')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        {/* Quota Warning */}
        {!isLoadingQuota && quotaInfo && (
          <QuotaWarning
            resourceType="products"
            usage={quotaInfo}
            severity={getQuotaSeverity()}
            dismissible={!isQuotaExceeded()}
          />
        )}

        {errors.submit && (
          <div className={styles.errorBanner}>
            <AlertCircle size={20} />
            <div>
              <p>{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.productForm}>
          {/* Navigation Tabs */}
          <div className={styles.tabNavigation}>
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>
            {activeTab === 'basic' && (
              <BasicInfoTab
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleDescriptionChange={handleDescriptionChange}
                handleCategorySelect={handleCategorySelect}
                handleTagsChange={handleTagsChange}
                handleBrandChange={handleBrandChange}
              />
            )}
            
            {activeTab === 'pricing' && (
              <PricingTab
                formData={formData}
                shops={shops}
                warehouses={warehouses}
                errors={errors}
                skuValidation={skuValidation}
                handleInputChange={handleInputChange}
                handleShopDataChange={handleShopDataChange}
                handleOpeningStockChange={handleOpeningStockChange}
              />
            )}
            
            {activeTab === 'attributes' && (
              <AttributesTab
                formData={formData}
                errors={errors}
                onAttributesChange={handleAttributesChange}
                onAttributeValuesChange={handleAttributeValuesChange}
              />
            )}
            
            {activeTab === 'variations' && (
              <VariationsTab
                formData={formData}
                attributes={attributes}
                shops={shops}
                warehouses={warehouses}
                errors={errors}
                skuValidation={skuValidation}
                variationCombinations={variationCombinations}
                expandedVariations={expandedVariations}
                selectedVariations={selectedVariations}
                showBulkActions={showBulkActions}
                bulkShopAssignments={bulkShopAssignments}
                bulkPrices={bulkPrices}
                bulkCostPrice={bulkCostPrice}
                bulkWeight={bulkWeight}
                setActiveTab={setActiveTab}
                toggleVariationExpansion={toggleVariationExpansion}
                toggleVariationSelection={toggleVariationSelection}
                selectAllVariations={selectAllVariations}
                deselectAllVariations={deselectAllVariations}
                removeSelectedVariations={removeSelectedVariations}
                regenerateVariations={regenerateVariations}
                handleBulkShopAssignment={handleBulkShopAssignment}
                handleBulkPriceChange={handleBulkPriceChange}
                setBulkCostPrice={setBulkCostPrice}
                setBulkWeight={setBulkWeight}
                setShowBulkActions={setShowBulkActions}
                applyBulkActions={() => applyBulkActions(success, error)}
                handleVariationChange={handleVariationChange}
                handleVariationShopDataChange={handleVariationShopDataChange}
                handleVariationStockChange={handleVariationStockChange}
                handleVariationImageChange={handleVariationImageChange}
              />
            )}
            
            {activeTab === 'bundle' && isBundle && (
              <BundleEditor
                bundleItems={formData.bundleItems}
                bundleShopIds={formData.bundleShopIds}
                shops={shops}
                errors={errors}
                onChange={handleBundleChange}
                formData={formData}
                skuValidation={skuValidation}
                handleInputChange={handleInputChange}
              />
            )}

            {activeTab === 'images' && (
              <ImagesTab
                images={formData.images}
                mainImageIndex={formData.mainImageIndex}
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                onSetMainImage={handleSetMainImage}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                errors={errors}
              />
            )}

            {activeTab === 'dropshipping' && showDropshippingTab && (
              formData.productType === 'variable' ? (
                <DropshippingVariationsSection
                  variations={formData.variations}
                  onVariationDropshippingChange={handleVariationDropshippingChange}
                  onBulkApply={applyBulkDropshipping}
                />
              ) : (
                <DropshippingSection
                  dropshippingEnabled={dropshippingData.dropshippingEnabled}
                  minimumSellingPrice={dropshippingData.minimumSellingPrice}
                  maximumSellingPrice={dropshippingData.maximumSellingPrice}
                  dropshippingPrice={dropshippingData.dropshippingPrice}
                  onChange={(field, value) => setDropshippingData((prev) => ({ ...prev, [field]: value }))}
                />
              )
            )}
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button 
              type="button" 
              onClick={() => navigate('/products')}
              className={styles.cancelButton}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isLoading || isQuotaExceeded()}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                <>
                  <Save size={18} />
                  {t('products.createProduct')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProductPage;